import React, { useState, useEffect } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import { View, Text, TouchableOpacity, TextInput, StyleSheet, Animated, Image } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useApi } from "../context/ApiContext";
import AppleDeveloperConfig from "./AppleDeveloperConfig";
import { useData } from "../context/DataContext";
const AppleDeveloperService = ({}) => {
	const { appId } = useParams();
	const { api } = useApi();
	const { refetchData, appData, teamData, loading } = useData();
	const [appConfig, setAppConfig] = useState(null);
	const [selectedTeam, setSelectedTeam] = useState(null);
	const [showTeamModal, setShowTeamModal] = useState(false);
	const [showConfigModal, setShowConfigModal] = useState(false);
	const [showCertGenModal, setShowCertGenModal] = useState(false);
	const [certGenStep, setCertGenStep] = useState(0);
	const [certGenError, setCertGenError] = useState(null);
	const [isGenerating, setIsGenerating] = useState(false);
	const [newTeam, setNewTeam] = useState({ name: "", description: "" });
	const [configStep, setConfigStep] = useState(1);
	const [configData, setConfigData] = useState({
		appleId: "",
		teamId: "",
		keyId: "",
		apiKey: "",
		apiKeyName: "",
		issuerId: "",
		bundleId: "",
	});
	const [error, setError] = useState(null);
	const [showDropdown, setShowDropdown] = useState(false);
	const [configSaving, setConfigSaving] = useState(false);
	const [currentTeamId, setCurrentTeamId] = useState(null);
	const [showCertDropdown, setShowCertDropdown] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [certToDelete, setCertToDelete] = useState(null);
	const [showProvProfileDropdown, setShowProvProfileDropdown] = useState(false);
	const [showProvProfileGenModal, setShowProvProfileGenModal] = useState(false);
	const [provProfileGenStep, setProvProfileGenStep] = useState(0);
	const [provProfileGenError, setProvProfileGenError] = useState(null);
	const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);
	const [bundleIdEditing, setBundleIdEditing] = useState(false);
	const [newBundleId, setNewBundleId] = useState("");
	const [isSavingBundleId, setIsSavingBundleId] = useState(false);
	const [appNameError, setAppNameError] = useState(null);
	const [appNameEditing, setAppNameEditing] = useState(false);
	const [newAppName, setNewAppName] = useState(appConfig?.name || "");
	const [slugEditing, setSlugEditing] = useState(false);
	const [newSlug, setNewSlug] = useState(appConfig?.slug || "");
	const [isSavingApp, setIsSavingApp] = useState(false);
	const [changelog, setChangelog] = useState("");
	const [changelogEditing, setChangelogEditing] = useState(false);
	const [changelogSaved, setChangelogSaved] = useState(false);
	const spinValue = new Animated.Value(0);
	const [icons, setIcons] = useState({
		light: null,
		dark: null,
		tinted: null,
	});
	const [iconErrors, setIconErrors] = useState({
		light: null,
		dark: null,
		tinted: null,
	});
	const [uploadingIcon, setUploadingIcon] = useState(null);
	const [iconsSaved, setIconsSaved] = useState(false);

	// Update app name and slug when appConfig changes
	useEffect(() => {
		if (appConfig) {
			setNewAppName(appConfig.name || "");
			setNewSlug(appConfig.slug || "");
		}
	}, [appConfig]);

	useEffect(() => {
		fetchConfig();
	}, [appId]);

	// Add effect to handle team selection when teams are loaded
	useEffect(() => {
		if (teamData.length > 0 && appConfig?.teamId) {
			const savedTeam = teamData.find((team) => team.id === appConfig.teamId);
			if (savedTeam) {
				setSelectedTeam(savedTeam);
				setCurrentTeamId(savedTeam.id);
			}
		}
	}, [teamData, appConfig?.teamId]);

	// Create the spinning animation
	useEffect(() => {
		if (configSaving) {
			Animated.loop(
				Animated.timing(spinValue, {
					toValue: 1,
					duration: 1000,
					useNativeDriver: true,
				})
			).start();
		} else {
			spinValue.setValue(0);
		}
	}, [configSaving]);

	const fetchConfig = async () => {
		try {
			const response = appData;
			// Find the apple_developer service in the services array
			const appleDeveloperService = response?.services?.find((service) => service.type === "apple_developer");
			const serviceConfig = appleDeveloperService?.config || {};

			// Set app config with either service data or defaults
			setAppConfig({
				name: serviceConfig.appName || response?.name || "",
				bundleId: serviceConfig.bundleId || "",
				provisioningProfiles: serviceConfig.provProfiles || [],
				selectedProfileId: serviceConfig.selectedProfile || null,
				selectedCert: serviceConfig.selectedCert || null,
				slug: serviceConfig.appSlug || "",
				teamId: serviceConfig.teamId || null,
				// Add icon data
				iconLight: serviceConfig.iconLight || null,
				iconDark: serviceConfig.iconDark || null,
				iconTinted: serviceConfig.iconTinted || null,
				// Add changelog
				changelog: serviceConfig.changelog || "",
			});

			// Set changelog state
			setChangelog(serviceConfig.changelog || "");

			fetchIcons();
		} catch (err) {
			console.error("Error fetching app config:", err);
			setError(err.message);
		}
	};

	const fetchIcons = async () => {
		try {
			const apiResponse = await api(`/app?projectId=${appId}&getImages=true`);

			if (apiResponse && apiResponse.length > 0 && apiResponse[0]) {
				const appDataWithIcons = apiResponse[0];
				setIcons({
					dark: appDataWithIcons.iconDark ? { url: appDataWithIcons.iconDark, type: "image/png", name: "dark-icon.png" } : null,
					light: appDataWithIcons.iconLight ? { url: appDataWithIcons.iconLight, type: "image/png", name: "light-icon.png" } : null,
					tinted: appDataWithIcons.iconTinted ? { url: appDataWithIcons.iconTinted, type: "image/png", name: "tinted-icon.png" } : null,
				});
			} else {
				// If response is empty or malformed, reset icons to null
				setIcons({ light: null, dark: null, tinted: null });
				console.warn(`No icon data returned for appId: ${appId}`);
			}
		} catch (error) {
			console.error("Failed to fetch icons:", error);
			setIcons({ light: null, dark: null, tinted: null }); // Reset on error
		}
	};

	const spin = spinValue.interpolate({
		inputRange: [0, 1],
		outputRange: ["0deg", "360deg"],
	});

	const handleCreateTeam = async () => {
		try {
			if (!newTeam.name.trim()) {
				setError("Team name is required");
				return;
			}

			const response = await api("/team", {
				method: "POST",
				body: {
					name: newTeam.name,
					description: newTeam.description,
				},
			});

			refetchData("team");
			setSelectedTeam(response);
			setShowTeamModal(false);
			setNewTeam({ name: "", description: "" });
			setError(null);
		} catch (err) {
			setError(err.message);
		}
	};

	const handleSaveConfig = async () => {
		try {
			setConfigSaving(true);
			// Create an object with only the fields that have been changed or are new
			const appStoreInfo = {};
			const currentConfig = selectedTeam?.services?.appStore || {};

			// Only include fields that are different from current config
			if (configData.appleId !== currentConfig.appleId) appStoreInfo.appleId = configData.appleId;
			if (configData.teamId !== currentConfig.teamId) appStoreInfo.teamId = configData.teamId;
			if (configData.keyId !== currentConfig.keyId) appStoreInfo.keyId = configData.keyId;
			if (configData.issuerId !== currentConfig.issuerId) appStoreInfo.issuerId = configData.issuerId;
			if (configData.bundleId !== currentConfig.bundleId) appStoreInfo.bundleId = configData.bundleId;

			// Only include apiKey and apiKeyName if a new file was uploaded
			if (configData.apiKey) {
				appStoreInfo.apiKey = configData.apiKey;
				appStoreInfo.apiKeyName = configData.apiKeyName;
			}

			await api("/team", {
				method: "PUT",
				body: {
					action: "AppleAppStore",
					teamId: selectedTeam.id,
					appStoreInfo,
				},
			});

			// Store the current team ID before fetching fresh data
			setCurrentTeamId(selectedTeam.id);

			// Fetch fresh team data
			await refetchData("team");

			setShowConfigModal(false);
			setConfigStep(1);
			setError(null);
		} catch (err) {
			setError(err.message);
		} finally {
			setConfigSaving(false);
		}
	};

	const handleEditConfig = () => {
		const currentConfig = selectedTeam?.services?.appStore || {};
		setConfigData({
			appleId: currentConfig.appleId || "",
			teamId: currentConfig.teamId || "",
			keyId: currentConfig.keyId || "",
			apiKey: "", // We don't get the actual key back
			apiKeyName: currentConfig.apiKeyName || "", // Keep the existing file name
			issuerId: currentConfig.issuerId || "",
			bundleId: currentConfig.bundleId || "",
		});
		setShowConfigModal(true);
	};

	const handleFileUpload = async (event) => {
		const file = event.target.files[0];
		if (file) {
			try {
				const reader = new FileReader();
				reader.onload = (e) => {
					setConfigData({
						...configData,
						apiKey: e.target.result,
						apiKeyName: file.name,
					});
				};
				reader.readAsText(file);
			} catch (err) {
				setError("Failed to read API key file");
			}
		}
	};

	const handleStartCertGeneration = async () => {
		try {
			setIsGenerating(true);
			setCertGenError(null);
			setCertGenStep(1);

			// Generate CSR
			const csrResponse = await api("/team", {
				method: "POST",
				body: {
					action: "generateCSR",
					teamId: selectedTeam.id,
					FunctionName: "CryptoFunction",
				},
			});

			if (!csrResponse.distroId) {
				throw new Error("Failed to generate CSR");
			}

			setCertGenStep(2);

			// Generate certificate
			const certResponse = await api("/team", {
				method: "POST",
				body: {
					action: "generateCert",
					teamId: selectedTeam.id,
					distroId: csrResponse.distroId,
					FunctionName: "CryptoFunction",
				},
			});

			if (!certResponse.certId) {
				throw new Error("Failed to generate certificate");
			}

			setCertGenStep(3);

			// Refresh team data to get updated certificates
			await refetchData("team");
		} catch (error) {
			setCertGenError(error.message || "Failed to generate certificate");
		} finally {
			setIsGenerating(false);
		}
	};

	const handleDeleteCertificate = (cert) => {
		setCertToDelete(cert);
		setShowDeleteConfirm(true);
	};

	const confirmDeleteCertificate = async () => {
		try {
			await api("/team", {
				method: "DELETE",
				body: {
					action: "deleteCert",
					teamId: selectedTeam.id,
					certId: certToDelete.certId,
					FunctionName: "CryptoFunction",
				},
			});

			// Refresh team data to get updated certificates
			await refetchData("team");
			setShowDeleteConfirm(false);
			setCertToDelete(null);
		} catch (error) {
			setError(error.message || "Failed to delete certificate");
		}
	};

	const handleBundleIdEdit = async () => {
		if (!bundleIdEditing) {
			setNewBundleId(selectedTeam?.services?.appStore?.bundleId || "");
			setBundleIdEditing(true);
			return;
		}

		try {
			setIsSavingBundleId(true);

			//UPDATEDIS
			await api("/team", {
				method: "PUT",
				body: {
					action: "AppleAppStore",
					teamId: selectedTeam.id,
					appStoreInfo: {
						bundleId: newBundleId,
					},
				},
			});

			// Store the current team ID before fetching fresh data
			setCurrentTeamId(selectedTeam.id);
			await refetchData("team");
			setBundleIdEditing(false);
		} catch (err) {
			setError(err.message);
		} finally {
			setIsSavingBundleId(false);
		}
	};

	const handleStartProfileGeneration = async () => {
		try {
			setIsGeneratingProfile(true);
			setProvProfileGenError(null);
			setAppNameError(null);

			if (!appId) {
				setProvProfileGenError("App ID is required but not found");
				return;
			}

			if (!appConfig?.name) {
				setAppNameError("App name is required. Please set the app name before generating a provisioning profile.");
				return;
			}

			if (!selectedTeam?.services?.appStore) {
				console.log(selectedTeam);
				setProvProfileGenError("Team is not configured for Apple services");
				return;
			}

			// Only proceed if we have valid data
			const response = await api("/app", {
				method: "POST",
				body: {
					action: "generate_profile",
					appId: appId,
					service: "apple_developer",
				},
			});

			// Store the current team ID before fetching fresh data
			setCurrentTeamId(selectedTeam.id);
			await refetchData("team");
			await fetchConfig(); // Refresh the app config after profile generation
			setShowProvProfileGenModal(false);
		} catch (err) {
			setProvProfileGenError(err.message);
		} finally {
			setIsGeneratingProfile(false);
		}
	};

	const handleAppInfoEdit = async (field) => {
		if (field === "name" && !appNameEditing) {
			setNewAppName(appConfig?.name || "");
			setAppNameEditing(true);
			return;
		}
		if (field === "slug" && !slugEditing) {
			setNewSlug(appConfig?.slug || "");
			setSlugEditing(true);
			return;
		}
		if (field === "bundleId" && !bundleIdEditing) {
			setNewBundleId(appConfig?.bundleId || "");
			setBundleIdEditing(true);
			return;
		}
		if (field === "changelog" && !changelogEditing) {
			setChangelogEditing(true);
			return;
		}

		try {
			setIsSavingApp(true);
			const updateData = {};

			// Only include the fields that are being explicitly edited
			if (field === "name" && appNameEditing) {
				updateData.appName = newAppName;
			} else if (field === "slug" && slugEditing) {
				updateData.appSlug = newSlug;
			} else if (field === "bundleId" && bundleIdEditing) {
				updateData.bundleId = newBundleId;
			} else if (field === "icons") {
				// Only include icon data when specifically saving icons
				if (icons.light) updateData.iconLight = icons.light.url;
				if (icons.dark) updateData.iconDark = icons.dark.url;
				if (icons.tinted) updateData.iconTinted = icons.tinted.url;
			} else if (field === "changelog" && changelogEditing) {
				updateData.changelog = changelog;
			}

			// Strip out null/empty values
			const cleanedUpdateData = Object.fromEntries(Object.entries(updateData).filter(([_, value]) => value !== null && value !== undefined && value !== ""));

			// Only proceed if we have valid data to update
			if (Object.keys(cleanedUpdateData).length > 0) {
				await api("/app", {
					method: "POST",
					body: {
						action: "update_app",
						appId: appId,
						updates: {
							service: "apple_developer",
							...cleanedUpdateData,
						},
					},
				});

				// Refresh the app config
				await fetchConfig();

				// Show success state for icons
				if (field === "icons") {
					setIconsSaved(true);
					// Reset saved indicator after 3 seconds
					setTimeout(() => {
						setIconsSaved(false);
					}, 3000);
				}

				// Show success state for changelog
				if (field === "changelog") {
					setChangelogSaved(true);
					// Reset saved indicator after 3 seconds
					setTimeout(() => {
						setChangelogSaved(false);
					}, 3000);
				}
			}

			if (field === "name") setAppNameEditing(false);
			if (field === "slug") setSlugEditing(false);
			if (field === "bundleId") setBundleIdEditing(false);
			if (field === "changelog") setChangelogEditing(false);
			// Don't reset icon states after save - we want to keep showing them
		} catch (err) {
			setError(err.message);
		} finally {
			setIsSavingApp(false);
		}
	};

	const handleTeamSelect = async (team) => {
		try {
			setSelectedTeam(team);
			setCurrentTeamId(team.id);
			setShowDropdown(false);
			// Reset any error states
			setError(null);
			// Reset any modal states
			setShowConfigModal(false);
			setShowCertGenModal(false);
			setShowProvProfileGenModal(false);
			// Reset any editing states
			setBundleIdEditing(false);
			setAppNameEditing(false);
			setSlugEditing(false);

			// Only send the teamId when updating the app
			const updateData = {
				teamId: team.id,
			};

			// Strip out null/empty values
			const cleanedUpdateData = Object.fromEntries(Object.entries(updateData).filter(([_, value]) => value !== null && value !== undefined && value !== ""));

			// Only proceed if we have valid data to update
			if (Object.keys(cleanedUpdateData).length > 0) {
				const response = await api("/app", {
					method: "POST",
					body: {
						action: "update_app",
						appId,
						updates: {
							service: "apple_developer",
							...cleanedUpdateData,
						},
					},
				});

				// Call the onUpdate callback if provided
				onUpdate?.(response);
			}
		} catch (err) {
			console.error("Error updating team:", err);
			setError(err.message);
		}
	};

	const renderTeamItem = (team) => {
		const appleConfig = team.services?.appStore || {};
		const services = team.services || {};
		const isError = appleConfig.status === "error" || appleConfig.status === "failed" || appleConfig.apiValidationError;

		const getServiceStatus = (serviceName, config) => {
			if (serviceName === "appStore") {
				// First check if the service is configured (has required fields)

				// Then check for error states
				if (config.status === "error" || config.status === "failed") return "error";
				if (config.active) return "active";
				return "inactive";
			}
			return "active";
		};

		return (
			<TouchableOpacity key={team.id} style={[styles.teamItem, selectedTeam?.id === team.id && styles.selectedTeamItem]} onPress={() => handleTeamSelect(team)}>
				<View style={styles.teamInfo}>
					<View style={styles.teamHeader}>
						<Text style={styles.teamName}>{team.name}</Text>
						<View style={[styles.roleChip, team.role === "owner" && styles.ownerRoleChip, team.role === "admin" && styles.adminRoleChip]}>
							<Text style={[styles.roleText, team.role === "owner" && styles.ownerRoleText, team.role === "admin" && styles.adminRoleText]}>{team.role || "member"}</Text>
						</View>
					</View>
					{team.description && <Text style={styles.teamDescription}>{team.description}</Text>}
					<View style={styles.servicesList}>
						{Object.entries(services).map(([serviceName, config]) => {
							const status = getServiceStatus(serviceName, config);
							return (
								<View key={serviceName} style={[styles.serviceChip, status === "active" && styles.activeServiceChip, status === "error" && styles.errorServiceChip]}>
									<View style={[styles.serviceDot, status === "active" && styles.activeServiceDot, status === "error" && styles.errorServiceDot, status === "inactive" && styles.inactiveServiceDot]} />
									<Text style={[styles.serviceText, status === "active" && styles.activeServiceText]}>{serviceName.replace(/_/g, " ")}</Text>
									{status === "error" && <Text style={styles.errorServiceText}>- Setup Error</Text>}
								</View>
							);
						})}
						{Object.keys(services).length === 0 && <Text style={styles.noServicesText}>No services configured</Text>}
					</View>
				</View>
			</TouchableOpacity>
		);
	};

	const renderConfigurationStatus = () => {
		if (!selectedTeam) return null;

		const appleConfig = selectedTeam.services?.appStore;
		const isError = appleConfig?.status === "error";

		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Apple Developer Configuration</Text>
					<TouchableOpacity style={styles.configButton} onPress={handleEditConfig}>
						<Ionicons name={appleConfig ? "create-outline" : "add-circle-outline"} size={20} color="#0A84FF" />
						<Text style={styles.configButtonText}>{appleConfig ? "Edit Configuration" : "Add Configuration"}</Text>
					</TouchableOpacity>
				</View>

				{appleConfig ? (
					<View style={[styles.configDetails, isError && styles.configDetailsError]}>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Apple ID</Text>
							<Text style={styles.configValue}>{appleConfig.appleId}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Team ID</Text>
							<Text style={styles.configValue}>{appleConfig.teamId}</Text>
						</View>
						<View style={{ ...styles.configItem, zIndex: 1000 }}>
							<Text style={styles.configLabel}>Distribution Certificate</Text>
							<View style={styles.certControlsContainer}>
								{appleConfig?.distroCerts?.length > 0 ? (
									<View style={styles.certDropdownContainer}>
										<TouchableOpacity style={[styles.certDropdown, showCertDropdown && styles.certDropdownActive]} onPress={() => setShowCertDropdown(!showCertDropdown)}>
											<Text style={styles.configValue}>
												{appConfig.selectedCert
													? (() => {
															const selectedCert = appleConfig.distroCerts.find((c) => c.distroId === appConfig.selectedCert);
															return selectedCert
																? `${selectedCert.name || "Distribution Certificate"}${selectedCert.serialNumber ? ` (...${selectedCert.serialNumber.slice(-6)})` : ""}`
																: `${appleConfig.distroCerts.length} certificate${appleConfig.distroCerts.length > 1 ? "s" : ""} available`;
													  })()
													: `${appleConfig.distroCerts.length} certificate${appleConfig.distroCerts.length > 1 ? "s" : ""} available`}
											</Text>
											<Ionicons name={showCertDropdown ? "chevron-up" : "chevron-down"} size={16} color="#666" />
										</TouchableOpacity>
										{showCertDropdown && (
											<View style={styles.certDropdownList}>
												{appleConfig.distroCerts.map((cert) => {
													const expiryDate = cert.expirationDate ? new Date(cert.expirationDate) : null;
													const formattedExpiry = expiryDate ? `${expiryDate.getMonth() + 1}/${expiryDate.getDate()}/${expiryDate.getFullYear()}` : "N/A";
													const serialSuffix = cert.serialNumber ? cert.serialNumber.slice(-6) : "N/A";
													const isSelected = cert.distroId === appConfig.selectedCert;

													return (
														<TouchableOpacity
															key={cert.distroId}
															style={[styles.certDropdownItem, isSelected && styles.certDropdownItemSelected]}
															onPress={async () => {
																try {
																	await api("/app", {
																		method: "POST",
																		body: {
																			action: "update_app",
																			appId,
																			updates: {
																				service: "apple_developer",
																				selectedCert: cert.distroId,
																			},
																		},
																	});
																	await fetchConfig();
																	setShowCertDropdown(false);
																} catch (err) {
																	setError(err.message);
																}
															}}>
															<View style={styles.certDropdownItemContent}>
																<Text style={[styles.certDropdownItemName, isSelected && styles.certDropdownItemNameSelected]}>
																	{cert.name || "Distribution Certificate"}
																	{cert.serialNumber ? ` (...${serialSuffix})` : ""}
																	{isSelected && " ✓"}
																</Text>
																<Text style={[styles.certDropdownItemExpiry, isSelected && styles.certDropdownItemExpirySelected]}>{cert.expirationDate ? `Expires: ${formattedExpiry}` : "Status: Pending"}</Text>
															</View>
														</TouchableOpacity>
													);
												})}
											</View>
										)}
									</View>
								) : null}
								<TouchableOpacity style={styles.generateCertButton} onPress={() => setShowCertGenModal(true)}>
									<Ionicons name="add-circle-outline" size={16} color="#0A84FF" />
									<Text style={styles.generateCertText}>Generate</Text>
								</TouchableOpacity>
							</View>
						</View>

						<View style={styles.configStatus}>{!isError && <Text style={[styles.statusText, appleConfig.active ? styles.activeStatusText : styles.inactiveStatusText]}>{appleConfig.active ? "Active" : "Inactive"}</Text>}</View>
						{isError && (
							<View style={styles.errorContainer}>
								<Text style={[styles.statusText, isError ? styles.errorStatusText : appleConfig.active ? styles.activeStatusText : styles.inactiveStatusText]}>{isError ? "Error" : appleConfig.active ? "Active" : "Inactive"}</Text>

								<Text style={styles.errorText}>{appleConfig.apiValidationError || appleConfig.errorMessage || "Configuration Error"}</Text>
							</View>
						)}
					</View>
				) : (
					<View style={styles.emptyConfig}>
						<Text style={styles.emptyConfigText}>No Apple Developer configuration found for this team.</Text>
						<Text style={styles.emptyConfigSubtext}>Add your Apple Developer credentials to enable app signing and distribution.</Text>
					</View>
				)}
			</View>
		);
	};

	const renderAppDeploymentSection = () => {
		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>App Deployment</Text>
				</View>
				<View style={styles.configDetails}>
					<View style={styles.configItem}>
						<Text style={styles.configLabel}>App Display Name</Text>
						<View style={styles.bundleIdContainer}>
							{appNameEditing ? (
								<TextInput style={styles.bundleIdInput} value={newAppName} onChangeText={setNewAppName} placeholder="Enter app name" placeholderTextColor="#666" />
							) : (
								<Text style={styles.configValue}>{appConfig?.name || "Not configured"}</Text>
							)}
							<TouchableOpacity style={[styles.bundleIdButton, isSavingApp && styles.bundleIdButtonDisabled]} onPress={() => handleAppInfoEdit("name")} disabled={isSavingApp}>
								{isSavingApp && appNameEditing ? (
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="sync" size={16} color="#fff" />
									</Animated.View>
								) : (
									<>
										<Ionicons name={appNameEditing ? "checkmark" : "create-outline"} size={16} color="#0A84FF" />
										<Text style={styles.bundleIdButtonText}>{appNameEditing ? "Save" : "Edit"}</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>

					<View style={styles.configItem}>
						<Text style={styles.configLabel}>App Icons (Square images required)</Text>
						<View style={styles.iconHelp}>
							<Ionicons name="information-circle-outline" size={16} color="#666" />
							<Text style={styles.iconHelpText}>Light mode is required. Dark and tinted are optional.</Text>
						</View>

						<View style={styles.iconContainer}>
							{/* Light Mode Icon - Required */}
							<View style={styles.iconItem}>
								<View style={[styles.iconPlaceholder, icons.light && styles.iconPreviewContainer]}>
									{icons.light ? <Image source={{ uri: icons.light.url }} style={styles.iconPreview} /> : <Ionicons name="image-outline" size={24} color="#666" />}
								</View>
								<Text style={styles.iconLabel}>
									Light Mode <Text style={styles.requiredText}>*</Text>
								</Text>
								{iconErrors.light && <Text style={styles.iconError}>{iconErrors.light}</Text>}
								<TouchableOpacity style={[styles.uploadIconButton, uploadingIcon === "light" && styles.uploadingIconButton]} onPress={() => document.getElementById("lightIconInput").click()}>
									{uploadingIcon === "light" ? (
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="sync" size={14} color="#0A84FF" />
										</Animated.View>
									) : (
										<Text style={styles.uploadIconText}>{icons.light ? "Change" : "Upload"}</Text>
									)}
								</TouchableOpacity>
								<input type="file" id="lightIconInput" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }} onChange={(e) => handleIconUpload(e, "light")} />
							</View>

							{/* Dark Mode Icon - Optional */}
							<View style={styles.iconItem}>
								<View style={[styles.iconPlaceholder, icons.dark && styles.iconPreviewContainer]}>
									{icons.dark ? <Image source={{ uri: icons.dark.url }} style={styles.iconPreview} /> : <Ionicons name="image-outline" size={24} color="#666" />}
								</View>
								<Text style={styles.iconLabel}>
									Dark Mode <Text style={styles.optionalText}>(Optional)</Text>
								</Text>
								{iconErrors.dark && <Text style={styles.iconError}>{iconErrors.dark}</Text>}
								<TouchableOpacity style={[styles.uploadIconButton, uploadingIcon === "dark" && styles.uploadingIconButton]} onPress={() => document.getElementById("darkIconInput").click()}>
									{uploadingIcon === "dark" ? (
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="sync" size={14} color="#0A84FF" />
										</Animated.View>
									) : (
										<Text style={styles.uploadIconText}>{icons.dark ? "Change" : "Upload"}</Text>
									)}
								</TouchableOpacity>
								<input type="file" id="darkIconInput" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }} onChange={(e) => handleIconUpload(e, "dark")} />
							</View>

							{/* Tinted Icon - Optional */}
							<View style={styles.iconItem}>
								<View style={[styles.iconPlaceholder, icons.tinted && styles.iconPreviewContainer]}>
									{icons.tinted ? <Image source={{ uri: icons.tinted.url }} style={styles.iconPreview} /> : <Ionicons name="image-outline" size={24} color="#666" />}
								</View>
								<Text style={styles.iconLabel}>
									Tinted <Text style={styles.optionalText}>(Optional)</Text>
								</Text>
								{iconErrors.tinted && <Text style={styles.iconError}>{iconErrors.tinted}</Text>}
								<TouchableOpacity style={[styles.uploadIconButton, uploadingIcon === "tinted" && styles.uploadingIconButton]} onPress={() => document.getElementById("tintedIconInput").click()}>
									{uploadingIcon === "tinted" ? (
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="sync" size={14} color="#0A84FF" />
										</Animated.View>
									) : (
										<Text style={styles.uploadIconText}>{icons.tinted ? "Change" : "Upload"}</Text>
									)}
								</TouchableOpacity>
								<input type="file" id="tintedIconInput" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }} onChange={(e) => handleIconUpload(e, "tinted")} />
							</View>
						</View>

						{/* Save Icons Button */}
						{(icons.light || icons.dark || icons.tinted) && (
							<View style={styles.saveIconsContainer}>
								<TouchableOpacity style={[styles.saveIconsButton, !icons.light && styles.saveIconsButtonDisabled, iconsSaved && styles.saveIconsButtonSuccess]} onPress={saveIcons} disabled={!icons.light || isSavingApp}>
									{isSavingApp ? (
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="sync" size={16} color="#fff" />
										</Animated.View>
									) : (
										<Text style={styles.saveIconsText}>{iconsSaved ? "Icons Saved!" : "Save Icons"}</Text>
									)}
								</TouchableOpacity>
								{iconsSaved && <Ionicons name="checkmark-circle" size={20} color="#34C759" style={styles.savedIcon} />}
							</View>
						)}
					</View>

					<View style={styles.configItem}>
						<Text style={styles.configLabel}>Changelog</Text>
						<View style={styles.changelogContainer}>
							<View style={styles.changelogContentContainer}>
								{changelogEditing ? (
									<TextInput style={styles.changelogInput} value={changelog} onChangeText={setChangelog} placeholder="Enter changelog details" placeholderTextColor="#666" multiline={true} numberOfLines={4} />
								) : (
									<View style={styles.changelogDisplayContainer}>
										<Text style={styles.changelogText}>{changelog || "No changelog provided"}</Text>
									</View>
								)}
								<View style={styles.changelogButtonContainer}>
									<TouchableOpacity
										style={[styles.changelogButton, changelogEditing && styles.changelogSaveButton, isSavingApp && changelogEditing && styles.changelogButtonDisabled, changelogSaved && styles.changelogButtonSuccess]}
										onPress={() => handleAppInfoEdit("changelog")}
										disabled={isSavingApp && changelogEditing}>
										{isSavingApp && changelogEditing ? (
											<Animated.View style={{ transform: [{ rotate: spin }] }}>
												<Ionicons name="sync" size={16} color="#fff" />
											</Animated.View>
										) : (
											<>
												<Ionicons name={changelogEditing ? "checkmark" : "create-outline"} size={16} color={changelogEditing ? "#fff" : "#0A84FF"} />
												<Text style={[styles.changelogButtonText, changelogEditing && styles.changelogSaveButtonText]}>{changelogSaved ? "Saved!" : changelogEditing ? "Save" : "Edit"}</Text>
											</>
										)}
									</TouchableOpacity>
									{changelogEditing && (
										<TouchableOpacity
											style={styles.changelogCancelButton}
											onPress={() => {
												setChangelogEditing(false);
												setChangelog(appConfig?.changelog || "");
											}}
											disabled={isSavingApp}>
											<Ionicons name="close" size={16} color="#666" />
											<Text style={styles.changelogCancelButtonText}>Cancel</Text>
										</TouchableOpacity>
									)}
								</View>
							</View>
						</View>
					</View>
				</View>
			</View>
		);
	};

	const renderProvisioningProfile = () => {
		if (!selectedTeam?.services?.appStore) return null;

		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Provisioning Profile</Text>
				</View>
				<View style={styles.configDetails}>
					<View style={styles.configItem}>
						<Text style={styles.configLabel}>App Slug</Text>
						<View style={styles.bundleIdContainer}>
							{slugEditing ? (
								<TextInput style={styles.bundleIdInput} value={newSlug} onChangeText={setNewSlug} placeholder="com.example.app" placeholderTextColor="#666" autoCapitalize="none" />
							) : (
								<Text style={styles.configValue}>{appConfig?.slug || "Not configured"}</Text>
							)}
							<TouchableOpacity style={[styles.bundleIdButton, isSavingApp && styles.bundleIdButtonDisabled]} onPress={() => handleAppInfoEdit("slug")} disabled={isSavingApp}>
								{isSavingApp && slugEditing ? (
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="sync" size={16} color="#fff" />
									</Animated.View>
								) : (
									<>
										<Ionicons name={slugEditing ? "checkmark" : "create-outline"} size={16} color="#0A84FF" />
										<Text style={styles.bundleIdButtonText}>{slugEditing ? "Save" : "Edit"}</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>

					<View style={styles.configItem}>
						<Text style={styles.configLabel}>Attached Bundle ID</Text>
						<View style={styles.bundleIdContainer}>
							{bundleIdEditing ? (
								<TextInput style={styles.bundleIdInput} value={newBundleId} onChangeText={setNewBundleId} placeholder="com.example.app" placeholderTextColor="#666" autoCapitalize="none" />
							) : (
								<Text style={styles.configValue}>{appConfig?.bundleId || "Not configured"}</Text>
							)}
							<TouchableOpacity style={[styles.bundleIdButton, isSavingBundleId && styles.bundleIdButtonDisabled]} onPress={() => handleAppInfoEdit("bundleId")} disabled={isSavingBundleId}>
								{isSavingBundleId ? (
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="sync" size={16} color="#fff" />
									</Animated.View>
								) : (
									<>
										<Ionicons name={bundleIdEditing ? "checkmark" : "create-outline"} size={16} color="#0A84FF" />
										<Text style={styles.bundleIdButtonText}>{bundleIdEditing ? "Save" : "Edit"}</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>
					<View style={{ ...styles.configItem, zIndex: 999 }}>
						<Text style={styles.configLabel}>Profile</Text>
						<View style={styles.certControlsContainer}>
							{appConfig?.provisioningProfiles?.length > 0 ? (
								<View style={styles.certDropdownContainer}>
									<TouchableOpacity style={[styles.certDropdown, showProvProfileDropdown && styles.certDropdownActive]} onPress={() => setShowProvProfileDropdown(!showProvProfileDropdown)}>
										<Text style={styles.configValue}>
											{appConfig.selectedProfileId
												? (() => {
														const selectedProfile = appConfig.provisioningProfiles.find((p) => p.id === appConfig.selectedProfileId);
														return selectedProfile
															? `${selectedProfile.name}${selectedProfile.uuid ? ` (...${selectedProfile.uuid.slice(-6)})` : ""}`
															: `${appConfig.provisioningProfiles.length} profile${appConfig.provisioningProfiles.length > 1 ? "s" : ""} available`;
												  })()
												: `${appConfig.provisioningProfiles.length} profile${appConfig.provisioningProfiles.length > 1 ? "s" : ""} available`}
										</Text>
										<Ionicons name={showProvProfileDropdown ? "chevron-up" : "chevron-down"} size={16} color="#666" />
									</TouchableOpacity>
									{showProvProfileDropdown && (
										<View style={styles.certDropdownList}>
											{appConfig.provisioningProfiles.map((profile) => {
												const expiryDate = profile.expiration ? new Date(profile.expiration) : null;
												const formattedExpiry = expiryDate ? `${expiryDate.getMonth() + 1}/${expiryDate.getDate()}/${expiryDate.getFullYear()}` : "N/A";
												const uuidSuffix = profile.uuid ? profile.uuid.slice(-6) : "N/A";
												const isSelected = profile.id === appConfig.selectedProfileId;

												return (
													<TouchableOpacity
														key={profile.uuid}
														style={[styles.certDropdownItem, isSelected && styles.certDropdownItemSelected]}
														onPress={async () => {
															try {
																setIsGeneratingProfile(true);
																await api("/app", {
																	method: "POST",
																	body: {
																		action: "update_app",
																		appId,
																		updates: {
																			service: "apple_developer",
																			selectedProfile: profile.id,
																		},
																	},
																});
																await fetchConfig();
															} catch (err) {
																setProvProfileGenError(err.message);
															} finally {
																setIsGeneratingProfile(false);
																setShowProvProfileDropdown(false);
															}
														}}>
														<View style={styles.certDropdownItemContent}>
															<Text style={[styles.certDropdownItemName, isSelected && styles.certDropdownItemNameSelected]}>
																{profile.name || "Provisioning Profile"}
																{profile.uuid ? ` (...${uuidSuffix})` : ""}
																{isSelected && " ✓"}
															</Text>
															<Text style={[styles.certDropdownItemExpiry, isSelected && styles.certDropdownItemExpirySelected]}>{profile.expiration ? `Expires: ${formattedExpiry}` : "Status: Pending"}</Text>
														</View>
													</TouchableOpacity>
												);
											})}
										</View>
									)}
								</View>
							) : (
								<View style={styles.emptyProfileContainer}>
									<Text style={styles.emptyProfileText}>No provisioning profile configured</Text>
								</View>
							)}
							<TouchableOpacity style={[styles.generateCertButton, isGeneratingProfile && styles.generateCertButtonDisabled]} onPress={handleStartProfileGeneration} disabled={isGeneratingProfile}>
								{isGeneratingProfile ? (
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="sync" size={16} color="#fff" />
									</Animated.View>
								) : (
									<>
										<Ionicons name="add-circle-outline" size={16} color="#0A84FF" />
										<Text style={styles.generateCertText}>Generate</Text>
									</>
								)}
							</TouchableOpacity>
						</View>
					</View>
					{appNameError && (
						<View style={styles.errorContainer}>
							<Text style={styles.errorText}>{appNameError}</Text>
						</View>
					)}
					{provProfileGenError && (
						<View style={styles.errorContainer}>
							<Text style={styles.errorText}>{provProfileGenError}</Text>
						</View>
					)}
				</View>
			</View>
		);
	};

	const renderCertGenModal = () => {
		const steps = [
			{ label: "Generating Certificate", description: "Creating CSR and requesting Distribution Certificate from Apple..." },
			{ label: "Saving Certificate", description: "Storing certificate in your account..." },
		];

		const renderExistingCerts = () => {
			const certs = selectedTeam?.services?.appStore?.distroCerts || [];
			if (certs.length === 0) return null;

			return (
				<View style={styles.existingCertsSection}>
					<Text style={styles.existingCertsTitle}>Existing Certificates</Text>
					<Text style={styles.existingCertsSubtitle}>Apple only allows 2 distribution certificates per account. You may need to delete one to generate a new certificate.</Text>

					<View style={styles.certsList}>
						{certs.map((cert) => {
							// Format expiration date
							const expiryDate = cert.expirationDate ? new Date(cert.expirationDate) : null;
							const formattedExpiry = expiryDate ? `${expiryDate.getMonth() + 1}/${expiryDate.getDate()}/${expiryDate.getFullYear()}` : "N/A";

							// Get last 6 digits of serial number if available
							const serialSuffix = cert.serialNumber ? cert.serialNumber.slice(-6) : "N/A";

							return (
								<View key={cert.distroId} style={styles.certItem}>
									<View style={styles.certInfo}>
										<Text style={styles.certName}>{cert.name || "Distribution Certificate"}</Text>
										<Text style={styles.certDetails}>
											{cert.serialNumber ? `Serial: ...${serialSuffix}` : ""}
											{cert.certId ? ` • ID: ${cert.certId}` : ""}
										</Text>
										<Text style={[styles.certExpiry, expiryDate && expiryDate < new Date() && styles.certExpired]}>{cert.expirationDate ? `Expires: ${formattedExpiry}` : "Status: Pending"}</Text>
									</View>
									<TouchableOpacity style={styles.deleteCertButton} onPress={() => handleDeleteCertificate(cert)}>
										<Ionicons name="trash-outline" size={16} color="#FF453A" />
										<Text style={styles.deleteCertText}>Delete</Text>
									</TouchableOpacity>
								</View>
							);
						})}
					</View>
				</View>
			);
		};

		return (
			<View style={styles.modalOverlay}>
				<View style={[styles.modal, styles.certGenModal]}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>Generate Distribution Certificate</Text>
						<TouchableOpacity
							onPress={() => {
								if (!isGenerating) {
									setShowCertGenModal(false);
									setCertGenStep(0);
									setCertGenError(null);
								}
							}}
							disabled={isGenerating}>
							<Ionicons name="close" size={24} color={isGenerating ? "#666666" : "#666"} />
						</TouchableOpacity>
					</View>
					<View style={styles.modalContent}>
						{!isGenerating && certGenStep === 0 && renderExistingCerts()}

						<View style={styles.certGenSteps}>
							{steps.map((step, index) => (
								<View key={step.label} style={styles.certGenStep}>
									<View style={styles.certGenStepHeader}>
										<View style={[styles.certGenStepDot, certGenStep > index + 1 && styles.certGenStepDotComplete, certGenStep === index + 1 && styles.certGenStepDotActive]}>
											{certGenStep > index + 1 ? (
												<Ionicons name="checkmark" size={12} color="#fff" />
											) : certGenStep === index + 1 ? (
												<Animated.View style={{ transform: [{ rotate: spin }] }}>
													<Ionicons name="sync" size={12} color="#fff" />
												</Animated.View>
											) : null}
										</View>
										<Text style={[styles.certGenStepLabel, certGenStep >= index + 1 && styles.certGenStepLabelActive]}>{step.label}</Text>
									</View>
									{certGenStep >= index + 1 && <Text style={styles.certGenStepDescription}>{step.description}</Text>}
								</View>
							))}
						</View>

						{certGenError && (
							<View style={styles.certGenError}>
								<Ionicons name="alert-circle" size={20} color="#FF453A" />
								<Text style={styles.certGenErrorText}>{certGenError}</Text>
							</View>
						)}

						<View style={styles.modalActions}>
							{!isGenerating && certGenStep === 0 && (
								<TouchableOpacity style={styles.createButton} onPress={handleStartCertGeneration}>
									<Text style={styles.createButtonText}>Generate Certificate</Text>
								</TouchableOpacity>
							)}
							{!isGenerating && certGenStep === 3 && (
								<TouchableOpacity
									style={styles.createButton}
									onPress={() => {
										setShowCertGenModal(false);
										setCertGenStep(0);
									}}>
									<Text style={styles.createButtonText}>Done</Text>
								</TouchableOpacity>
							)}
						</View>
					</View>
				</View>

				{/* Delete Confirmation Modal */}
				{showDeleteConfirm && (
					<View style={styles.confirmModalOverlay}>
						<View style={styles.confirmModal}>
							<View style={styles.confirmModalContent}>
								<Ionicons name="warning" size={32} color="#FF453A" />
								<Text style={styles.confirmModalTitle}>Delete Certificate?</Text>
								<Text style={styles.confirmModalText}>Are you sure you want to delete this distribution certificate? This action cannot be undone.</Text>
								<Text style={styles.certNameText}>{certToDelete?.name}</Text>
							</View>
							<View style={styles.confirmModalActions}>
								<TouchableOpacity
									style={styles.cancelButton}
									onPress={() => {
										setShowDeleteConfirm(false);
										setCertToDelete(null);
									}}>
									<Text style={styles.cancelButtonText}>Cancel</Text>
								</TouchableOpacity>
								<TouchableOpacity style={[styles.deleteButton]} onPress={confirmDeleteCertificate}>
									<Text style={styles.deleteButtonText}>Delete Certificate</Text>
								</TouchableOpacity>
							</View>
						</View>
					</View>
				)}
			</View>
		);
	};

	const HelpTooltip = ({ content }) => {
		const [isVisible, setIsVisible] = useState(false);

		return (
			<View style={styles.tooltipContainer}>
				<TouchableOpacity style={styles.helpIcon} onPress={() => setIsVisible(!isVisible)}>
					<Ionicons name="information-circle" size={20} color="#0A84FF" />
					{isVisible && <View style={styles.tooltipContent}>{content}</View>}
				</TouchableOpacity>
			</View>
		);
	};

	// Add this function for icon validation and handling
	const handleIconUpload = async (event, type) => {
		const file = event.target.files[0];
		if (!file) return;

		setUploadingIcon(type);
		setIconErrors({ ...iconErrors, [type]: null });

		// Check file size (5MB limit)
		if (file.size > 5 * 1024 * 1024) {
			setIconErrors({ ...iconErrors, [type]: "Max size: 5MB" });
			setUploadingIcon(null);
			return;
		}

		// Check file type
		if (!file.type.match("image/(jpeg|jpg|png)")) {
			setIconErrors({ ...iconErrors, [type]: "JPG or PNG only" });
			setUploadingIcon(null);
			return;
		}

		try {
			// Convert to base64 for preview and storage
			const reader = new FileReader();

			reader.onload = (e) => {
				// Create image data URL from file
				const imageDataUrl = e.target.result;

				// Create an offscreen element to check dimensions
				// Note: This approach works with React Native Web
				const img = document.createElement("img");

				img.onload = () => {
					// Check dimensions - just verify it's square
					if (img.width !== img.height) {
						setIconErrors({ ...iconErrors, [type]: `Image must be square (got ${img.width}×${img.height})` });
						setUploadingIcon(null);
						return;
					}

					// If dimensions are correct, save the image data
					setIcons({
						...icons,
						[type]: {
							url: imageDataUrl,
							name: file.name,
							size: file.size,
							type: file.type,
						},
					});
					setUploadingIcon(null);
				};

				img.onerror = () => {
					setIconErrors({ ...iconErrors, [type]: "Invalid or corrupted image" });
					setUploadingIcon(null);
				};

				// Set the source to trigger the load
				img.src = imageDataUrl;
			};

			reader.onerror = () => {
				console.error("FileReader error:", reader.error);
				setIconErrors({ ...iconErrors, [type]: "Failed to read file" });
				setUploadingIcon(null);
			};

			reader.readAsDataURL(file);
		} catch (err) {
			console.error("Error processing image:", err);
			setIconErrors({ ...iconErrors, [type]: "Failed to process image" });
			setUploadingIcon(null);
		}
	};

	const saveIcons = async () => {
		try {
			await handleAppInfoEdit("icons");
		} catch (err) {
			setError(err.message || "Failed to save icons");
		}
	};

	if (loading.team) {
		return (
			<View style={[styles.container, styles.centerContent]}>
				<Text style={styles.loadingText}>Loading teams...</Text>
			</View>
		);
	}

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.header}>
					<Text style={styles.title}>Apple Core Service</Text>
					<Text style={styles.subtitle}>Configure your Apple Developer account and API credentials. This allows you to publish your app to the App Store.</Text>
				</View>
			</View>

			<View style={styles.content}>
				<View style={styles.teamSelector}>
					<Text style={styles.label}>Select Team</Text>
					<View style={styles.dropdownContainer}>
						<TouchableOpacity style={[styles.dropdown, showDropdown && styles.dropdownActive]} onPress={() => setShowDropdown(!showDropdown)}>
							<View style={styles.dropdownContent}>
								{selectedTeam ? (
									<>
										<Text style={styles.dropdownText}>{selectedTeam.name}</Text>
										<Text style={styles.dropdownSubtext}>{selectedTeam.description || "No description"}</Text>
									</>
								) : (
									<Text style={styles.dropdownPlaceholder}>Select a team</Text>
								)}
							</View>
							<Ionicons name={showDropdown ? "chevron-up" : "chevron-down"} size={20} color="#666" />
						</TouchableOpacity>
						{showDropdown && (
							<View style={styles.dropdownList}>
								{teamData.map(renderTeamItem)}
								<TouchableOpacity
									style={styles.createTeamButton}
									onPress={() => {
										setShowDropdown(false);
										setShowTeamModal(true);
									}}>
									<Ionicons name="add-circle-outline" size={20} color="#0A84FF" />
									<Text style={styles.createTeamText}>Create New Team</Text>
								</TouchableOpacity>
							</View>
						)}
					</View>
				</View>

				{error && (
					<View style={styles.errorContainer}>
						<Text style={styles.errorText}>{error}</Text>
					</View>
				)}

				{renderAppDeploymentSection()}
				{renderConfigurationStatus()}
				{renderProvisioningProfile()}
			</View>

			{/* Configuration Modal */}
			{showConfigModal && (
				<View style={styles.modalOverlay}>
					<View style={styles.modal}>
						<View style={styles.modalHeader}>
							<Text style={styles.modalTitle}>{selectedTeam?.services?.appStore?.active ? "Edit Apple Developer Configuration" : "Add Apple Developer Configuration"}</Text>
							<TouchableOpacity
								onPress={() => {
									setShowConfigModal(false);
									setConfigStep(1);
								}}>
								<Ionicons name="close" size={24} color="#666" />
							</TouchableOpacity>
						</View>
						<View style={styles.modalContent}>
							<View style={styles.stepIndicator}>
								<View style={[styles.stepDot, configStep >= 1 && styles.activeStepDot]} />
								<View style={[styles.stepLine, configStep >= 2 && styles.activeStepLine]} />
								<View style={[styles.stepDot, configStep >= 2 && styles.activeStepDot]} />
								<View style={[styles.stepLine, configStep >= 3 && styles.activeStepLine]} />
								<View style={[styles.stepDot, configStep >= 3 && styles.activeStepDot]} />
							</View>

							<Text style={styles.stepTitle}>
								{configStep === 1 && "Apple Developer Account"}
								{configStep === 2 && "API Configuration"}
								{configStep === 3 && "Bundle Configuration"}
							</Text>

							{configStep === 1 && (
								<>
									<Text style={styles.stepDescription}>Enter your Apple Developer account details</Text>
									<View style={styles.helpSection}>
										<View style={styles.helpHeader}>
											<Ionicons name="information-circle" size={20} color="#0A84FF" />
											<Text style={styles.helpHeaderText}>How to find these details:</Text>
										</View>
										<View style={styles.helpContent}>
											<View style={styles.helpItem}>
												<Text style={styles.helpLabel}>Apple ID:</Text>
												<Text style={styles.helpText}>Your Apple Developer account email address</Text>
											</View>
											<View style={styles.helpItem}>
												<Text style={styles.helpLabel}>Team ID:</Text>
												<Text style={styles.helpText}>Found in the Membership section of your Apple Developer account</Text>
												<TouchableOpacity onPress={() => window.open("https://developer.apple.com/account/#/membership", "_blank")} style={styles.helpLink}>
													<Text style={styles.helpLinkText}>→ View in Apple Developer Portal</Text>
												</TouchableOpacity>
											</View>
										</View>
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>Apple ID</Text>
										<TextInput
											style={styles.formInput}
											value={configData.appleId}
											onChangeText={(text) => setConfigData({ ...configData, appleId: text })}
											placeholder="Enter your Apple ID"
											placeholderTextColor="#666"
											autoCapitalize="none"
											keyboardType="email-address"
										/>
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>Team ID</Text>
										<TextInput style={styles.formInput} value={configData.teamId} onChangeText={(text) => setConfigData({ ...configData, teamId: text })} placeholder="Enter your Team ID" placeholderTextColor="#666" autoCapitalize="none" />
									</View>
								</>
							)}

							{configStep === 2 && (
								<>
									<Text style={styles.stepDescription}>Configure your API access credentials</Text>
									<View style={styles.helpSection}>
										<View style={styles.helpHeader}>
											<Ionicons name="information-circle" size={20} color="#0A84FF" />
											<Text style={styles.helpHeaderText}>How to get your API credentials:</Text>
										</View>
										<View style={styles.helpContent}>
											<View style={styles.helpItem}>
												<Text style={styles.helpLabel}>Key ID & API Key:</Text>
												<Text style={styles.helpText}>1. Go to Keys section in App Store Connect</Text>
												<Text style={styles.helpText}>2. Create a new key with App Store Connect API access</Text>
												<Text style={styles.helpText}>3. Download the P8 file and Copy the Key ID</Text>
												<TouchableOpacity onPress={() => window.open("https://appstoreconnect.apple.com/access/integrations/api", "_blank")} style={styles.helpLink}>
													<Text style={styles.helpLinkText}>→ Generate API Key</Text>
												</TouchableOpacity>
											</View>
											<View style={styles.helpItem}>
												<Text style={styles.helpLabel}>Issuer ID:</Text>
												<Text style={styles.helpText}>Found in Users and Access {">"} Keys section</Text>
												<TouchableOpacity onPress={() => window.open("https://appstoreconnect.apple.com/access/integrations/api", "_blank")} style={styles.helpLink}>
													<Text style={styles.helpLinkText}>→ View Issuer ID</Text>
												</TouchableOpacity>
											</View>
										</View>
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>Key ID</Text>
										<TextInput style={styles.formInput} value={configData.keyId} onChangeText={(text) => setConfigData({ ...configData, keyId: text })} placeholder="Enter your Key ID" placeholderTextColor="#666" autoCapitalize="none" />
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>API Key (P8 File)</Text>
										<View style={styles.fileUploadContainer}>
											<TouchableOpacity style={[styles.fileUploadButton, (configData.apiKeyName || selectedTeam?.services?.appStore?.apiKeyName) && styles.fileUploadButtonWithFile]} onPress={() => document.getElementById("apiKeyFile").click()}>
												<Ionicons name={configData.apiKeyName || selectedTeam?.services?.appStore?.apiKeyName ? "document-text" : "cloud-upload"} size={20} color="#fff" />
												<Text style={styles.fileUploadText}>{configData.apiKeyName || selectedTeam?.services?.appStore?.apiKeyName || "Upload P8 File"}</Text>
											</TouchableOpacity>
											<input type="file" id="apiKeyFile" accept=".p8" style={{ display: "none" }} onChange={handleFileUpload} />
										</View>
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>Issuer ID</Text>
										<TextInput style={styles.formInput} value={configData.issuerId} onChangeText={(text) => setConfigData({ ...configData, issuerId: text })} placeholder="Enter your Issuer ID" placeholderTextColor="#666" autoCapitalize="none" />
									</View>
								</>
							)}

							{configStep === 3 && (
								<>
									<Text style={styles.stepDescription}>Set your application bundle identifier</Text>
									<View style={styles.helpSection}>
										<View style={styles.helpHeader}>
											<Ionicons name="information-circle" size={20} color="#0A84FF" />
											<Text style={styles.helpHeaderText}>About Bundle ID:</Text>
										</View>
										<View style={styles.helpContent}>
											<View style={styles.helpItem}>
												<Text style={styles.helpLabel}>Bundle ID Format:</Text>
												<Text style={styles.helpText}>• Format: com.company.appname</Text>
												<Text style={styles.helpText}>• Must match your Xcode project</Text>
												<Text style={styles.helpText}>• Register in Certificates, IDs & Profiles section</Text>
												<TouchableOpacity onPress={() => window.open("https://developer.apple.com/account/resources/identifiers/list", "_blank")} style={styles.helpLink}>
													<Text style={styles.helpLinkText}>→ Register Bundle ID</Text>
												</TouchableOpacity>
											</View>
										</View>
									</View>
									<View style={styles.formGroup}>
										<Text style={styles.formLabel}>Bundle ID</Text>
										<TextInput style={styles.formInput} value={configData.bundleId} onChangeText={(text) => setConfigData({ ...configData, bundleId: text })} placeholder="com.example.app" placeholderTextColor="#666" autoCapitalize="none" />
									</View>
								</>
							)}

							<View style={styles.modalActions}>
								{configStep > 1 && (
									<TouchableOpacity style={styles.secondaryButton} onPress={() => setConfigStep(configStep - 1)}>
										<Ionicons name="arrow-back" size={20} color="#fff" />
										<Text style={styles.secondaryButtonText}>Previous</Text>
									</TouchableOpacity>
								)}

								{configStep < 3 ? (
									<TouchableOpacity style={[styles.createButton, !configData.appleId && configStep === 1 && styles.createButtonDisabled]} onPress={() => setConfigStep(configStep + 1)} disabled={!configData.appleId && configStep === 1}>
										<View style={styles.buttonContent}>
											<Text style={styles.createButtonText}>Next</Text>
											<Ionicons name="arrow-forward" size={20} color="#fff" />
										</View>
									</TouchableOpacity>
								) : (
									<TouchableOpacity style={[styles.createButton, configSaving && styles.createButtonDisabled]} onPress={handleSaveConfig} disabled={configSaving}>
										<View style={styles.buttonContent}>
											{configSaving ? (
												<Animated.View style={{ transform: [{ rotate: spin }] }}>
													<Ionicons name="sync" size={20} color="#fff" />
												</Animated.View>
											) : (
												<Text style={styles.createButtonText}>Save Configuration</Text>
											)}
										</View>
									</TouchableOpacity>
								)}
							</View>
						</View>
					</View>
				</View>
			)}

			{/* Create Team Modal */}
			{showTeamModal && (
				<View style={styles.modalOverlay}>
					<View style={styles.modal}>
						<View style={styles.modalHeader}>
							<Text style={styles.modalTitle}>Create New Team</Text>
							<TouchableOpacity onPress={() => setShowTeamModal(false)}>
								<Ionicons name="close" size={24} color="#666" />
							</TouchableOpacity>
						</View>
						<View style={styles.modalContent}>
							<View style={styles.formGroup}>
								<Text style={styles.formLabel}>Team Name</Text>
								<TextInput style={styles.formInput} value={newTeam.name} onChangeText={(text) => setNewTeam({ ...newTeam, name: text })} placeholder="Enter team name" placeholderTextColor="#666" />
							</View>
							<View style={styles.formGroup}>
								<Text style={styles.formLabel}>Description (Optional)</Text>
								<TextInput style={styles.formInput} value={newTeam.description} onChangeText={(text) => setNewTeam({ ...newTeam, description: text })} placeholder="Enter team description" placeholderTextColor="#666" multiline />
							</View>
							<View style={styles.modalActions}>
								<TouchableOpacity style={styles.cancelButton} onPress={() => setShowTeamModal(false)}>
									<Text style={styles.cancelButtonText}>Cancel</Text>
								</TouchableOpacity>
								<TouchableOpacity style={[styles.createButton, !newTeam.name && styles.createButtonDisabled]} onPress={handleCreateTeam} disabled={!newTeam.name}>
									<Text style={styles.createButtonText}>Create Team</Text>
								</TouchableOpacity>
							</View>
						</View>
					</View>
				</View>
			)}

			{showCertGenModal && renderCertGenModal()}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 20,
	},
	centerContent: {
		justifyContent: "center",
		alignItems: "center",
	},
	loadingText: {
		color: "#fff",
		fontSize: 16,
	},
	header: {
		marginBottom: 24,
	},
	title: {
		fontSize: 24,
		fontWeight: "700",
		color: "#fff",
		marginBottom: 8,
	},
	subtitle: {
		fontSize: 14,
		color: "#999",
		lineHeight: 20,
	},
	content: {
		flex: 1,
		position: "relative",
		zIndex: 1,
	},
	teamSelector: {
		marginBottom: 24,
		position: "relative",
		zIndex: 2,
	},
	label: {
		fontSize: 14,
		color: "#fff",
		marginBottom: 8,
		fontWeight: "500",
	},
	dropdownContainer: {
		position: "relative",
		zIndex: 2,
	},
	dropdown: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 16,
		borderWidth: 1,
		borderColor: "#404040",
		position: "relative",
		zIndex: 2,
	},
	dropdownActive: {
		borderColor: "#0A84FF",
		backgroundColor: "#0A84FF10",
	},
	dropdownContent: {
		flex: 1,
	},
	dropdownText: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "500",
	},
	dropdownSubtext: {
		color: "#666",
		fontSize: 13,
		marginTop: 2,
	},
	dropdownPlaceholder: {
		color: "#666",
		fontSize: 15,
	},
	dropdownList: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		marginTop: 8,
		maxHeight: 400,
		overflow: "auto",
		zIndex: 2,
		borderWidth: 1,
		borderColor: "#404040",
		padding: 8,
	},
	teamItem: {
		padding: 16,
		borderRadius: 8,
		marginBottom: 4,
		position: "relative",
		zIndex: 1085,
	},
	selectedTeamItem: {
		backgroundColor: "#0A84FF15",
	},
	teamHeader: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 6,
	},
	teamName: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "600",
	},
	roleChip: {
		backgroundColor: "#66666620",
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 12,
	},
	ownerRoleChip: {
		backgroundColor: "#FF375F20",
	},
	adminRoleChip: {
		backgroundColor: "#0A84FF20",
	},
	roleText: {
		color: "#666",
		fontSize: 12,
		fontWeight: "600",
		textTransform: "capitalize",
	},
	ownerRoleText: {
		color: "#FF375F",
	},
	adminRoleText: {
		color: "#0A84FF",
	},
	teamDescription: {
		color: "#666",
		fontSize: 13,
		marginBottom: 8,
		lineHeight: 18,
	},
	servicesList: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 6,
		marginTop: 8,
		position: "relative",
		zIndex: 1085,
	},
	serviceChip: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#40404040",
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 12,
		gap: 6,
	},
	activeServiceChip: {
		backgroundColor: "#82C91E20",
	},
	serviceDot: {
		width: 6,
		height: 6,
		borderRadius: 3,
	},
	activeServiceDot: {
		backgroundColor: "#82C91E",
	},
	inactiveServiceDot: {
		backgroundColor: "#666",
	},
	serviceText: {
		color: "#666",
		fontSize: 12,
		fontWeight: "500",
		textTransform: "capitalize",
	},
	activeServiceText: {
		color: "#82C91E",
	},
	noServicesText: {
		color: "#666",
		fontSize: 13,
		fontStyle: "italic",
	},
	createTeamButton: {
		flexDirection: "row",
		alignItems: "center",
		padding: 16,
		gap: 8,
		borderTopWidth: 1,
		borderTopColor: "#404040",
		marginTop: 8,
	},
	createTeamText: {
		color: "#0A84FF",
		fontSize: 14,
		fontWeight: "500",
	},
	errorContainer: {
		backgroundColor: "#FF453A15",
		padding: 12,
		borderRadius: 8,
		marginTop: 8,
	},
	errorText: {
		color: "#FF453A",
		fontSize: 14,
	},
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.3)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 3,
	},
	modal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		width: "60%",
		maxWidth: 600,
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	modalTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	modalContent: {
		padding: 20,
	},
	formGroup: {
		marginBottom: 20,
	},
	formLabel: {
		color: "#fff",
		fontSize: 14,
		marginBottom: 8,
	},
	formInput: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
	},
	modalActions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
		marginTop: 24,
	},
	cancelButton: {
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#333",
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	createButton: {
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
		minWidth: 100,
	},
	buttonContent: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	createButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	createButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	configSection: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
		marginTop: 24,
		position: "relative",
		zIndex: 1,
	},
	configHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	configTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
	},
	configButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 8,
		borderRadius: 8,
		backgroundColor: "#0A84FF15",
	},
	configButtonText: {
		color: "#0A84FF",
		fontSize: 14,
		fontWeight: "500",
	},
	configDetails: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 16,
	},
	configItem: {
		marginBottom: 12,
	},
	configLabel: {
		color: "#666",
		fontSize: 12,
		marginBottom: 4,
	},
	configValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
		flex: 1,
		marginRight: 8,
	},
	configStatus: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		marginTop: 16,
		paddingTop: 16,
		borderTopWidth: 1,
		borderTopColor: "#404040",
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: 4,
	},
	activeStatusDot: {
		backgroundColor: "#82C91E",
	},
	statusText: {
		color: "#82C91E",
		fontSize: 14,
		fontWeight: "500",
	},
	emptyConfig: {
		padding: 24,
		alignItems: "center",
		backgroundColor: "#33333350",
		borderRadius: 8,
	},
	emptyConfigText: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "500",
		marginBottom: 8,
		textAlign: "center",
	},
	emptyConfigSubtext: {
		color: "#666",
		fontSize: 13,
		textAlign: "center",
		lineHeight: 18,
	},
	stepIndicator: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: 24,
	},
	stepDot: {
		width: 12,
		height: 12,
		borderRadius: 6,
		backgroundColor: "#404040",
	},
	activeStepDot: {
		backgroundColor: "#0A84FF",
	},
	stepLine: {
		flex: 1,
		height: 2,
		backgroundColor: "#404040",
		marginHorizontal: 8,
	},
	activeStepLine: {
		backgroundColor: "#0A84FF",
	},
	stepTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 8,
		textAlign: "center",
	},
	stepDescription: {
		color: "#666",
		fontSize: 14,
		marginBottom: 24,
		textAlign: "center",
	},
	secondaryButton: {
		flexDirection: "row",
		alignItems: "center",
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#404040",
		gap: 8,
	},
	secondaryButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	appleConfigInfo: {
		marginTop: 8,
		marginBottom: 8,
		padding: 8,
		backgroundColor: "#40404040",
		borderRadius: 8,
	},
	appleConfigError: {
		backgroundColor: "#FF453A15",
		borderWidth: 1,
		borderColor: "#FF453A30",
	},
	appleConfigText: {
		color: "#999",
		fontSize: 12,
		fontFamily: "monospace",
		marginBottom: 2,
	},
	appleConfigErrorText: {
		color: "#FF453A",
	},
	errorMessage: {
		color: "#FF453A",
		fontSize: 12,
		fontWeight: "500",
		marginTop: 4,
	},
	errorServiceChip: {
		backgroundColor: "#FF453A20",
	},
	errorServiceDot: {
		backgroundColor: "#FF453A",
	},
	errorServiceText: {
		color: "#FF453A",
	},
	configDetailsError: {
		backgroundColor: "#FF453A10",
		borderWidth: 1,
		borderColor: "#FF453A30",
	},
	inactiveStatusDot: {
		backgroundColor: "#666",
	},
	inactiveStatusText: {
		color: "#666",
	},
	errorStatusDot: {
		backgroundColor: "#FF453A",
	},
	errorStatusText: {
		color: "#FF453A",
	},
	activeStatusText: {
		color: "#82C91E",
	},
	fileUploadContainer: {
		width: "100%",
	},
	fileUploadButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#0A84FF",
		padding: 12,
		borderRadius: 8,
		gap: 8,
	},
	fileUploadButtonWithFile: {
		backgroundColor: "#34C759",
	},
	fileUploadText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	tooltipContainer: {
		alignItems: "center",
		marginBottom: 20,
	},
	helpIcon: {
		position: "relative",
		padding: 8,
		zIndex: 1001,
	},
	tooltipContent: {
		position: "absolute",
		backgroundColor: "#1C1C1E",
		padding: 16,
		borderRadius: 12,
		width: 300,
		top: "100%",
		left: "50%",
		transform: [{ translateX: -150 }],
		marginTop: 8,
		borderWidth: 1,
		borderColor: "#333",
		zIndex: 1000,
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.25,
		shadowRadius: 4,
	},
	tooltipText: {
		color: "#fff",
		fontSize: 14,
	},
	tooltipTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
		marginBottom: 8,
	},
	tooltipItem: {
		color: "#999",
		fontSize: 13,
		marginBottom: 4,
		lineHeight: 18,
	},
	tooltipLink: {
		color: "#0A84FF",
		fontSize: 13,
		marginTop: 8,
		textDecorationLine: "underline",
	},
	helpSection: {
		backgroundColor: "#1C1C1E",
		borderRadius: 12,
		padding: 16,
		marginBottom: 24,
		borderWidth: 1,
		borderColor: "#333",
	},
	helpHeader: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 12,
		gap: 8,
	},
	helpHeaderText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	helpContent: {
		gap: 16,
	},
	helpItem: {
		marginBottom: 12,
	},
	helpLabel: {
		color: "#0A84FF",
		fontSize: 13,
		fontWeight: "500",
		marginBottom: 4,
	},
	helpText: {
		color: "#999",
		fontSize: 13,
		lineHeight: 18,
	},
	helpLink: {
		marginTop: 8,
	},
	helpLinkText: {
		color: "#0A84FF",
		fontSize: 13,
		textDecorationLine: "underline",
	},
	certControlsContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 12,
		marginTop: 4,
		position: "relative",
		zIndex: 1,
	},
	certDropdownContainer: {
		flex: 1,
		position: "relative",
		zIndex: 1,
	},
	certDropdown: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#333",
		borderRadius: 6,
		padding: 8,
		paddingHorizontal: 12,
		position: "relative",
		zIndex: 1,
	},
	certDropdownActive: {
		borderColor: "#0A84FF",
		backgroundColor: "#0A84FF10",
	},
	generateCertButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		backgroundColor: "#0A84FF15",
		padding: 8,
		paddingHorizontal: 12,
		borderRadius: 6,
		height: 36,
	},
	generateCertText: {
		color: "#0A84FF",
		fontSize: 13,
		fontWeight: "500",
	},
	certDropdownList: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		backgroundColor: "#333",
		borderRadius: 8,
		marginTop: 4,
		maxHeight: 200,
		overflow: "auto",
		zIndex: 1,
		borderWidth: 1,
		borderColor: "#404040",
	},
	certDropdownItem: {
		padding: 12,
		borderBottomWidth: 1,
		borderBottomColor: "#404040",
	},
	certDropdownItemContent: {
		gap: 4,
	},
	certDropdownItemName: {
		color: "#fff",
		fontSize: 13,
		fontFamily: "monospace",
	},
	certDropdownItemExpiry: {
		color: "#666",
		fontSize: 12,
	},
	certGenModal: {
		maxWidth: 480,
		width: "90%",
	},
	certGenSteps: {
		padding: 24,
	},
	certGenStep: {
		marginBottom: 24,
	},
	certGenStepHeader: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 8,
	},
	certGenStepDot: {
		width: 24,
		height: 24,
		borderRadius: 12,
		backgroundColor: "#404040",
		marginRight: 12,
		alignItems: "center",
		justifyContent: "center",
	},
	certGenStepDotActive: {
		backgroundColor: "#0A84FF",
	},
	certGenStepDotComplete: {
		backgroundColor: "#34C759",
	},
	certGenStepLabel: {
		fontSize: 16,
		fontWeight: "600",
		color: "#666",
	},
	certGenStepLabelActive: {
		color: "#fff",
	},
	certGenStepDescription: {
		color: "#999",
		fontSize: 14,
		marginLeft: 36,
	},
	certGenError: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#FF453A15",
		padding: 12,
		borderRadius: 8,
		marginTop: 8,
		gap: 8,
	},
	certGenErrorText: {
		color: "#FF453A",
		flex: 1,
	},
	existingCertsSection: {
		backgroundColor: "#1C1C1E",
		borderRadius: 12,
		padding: 16,
		marginBottom: 24,
	},
	existingCertsTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 8,
	},
	existingCertsSubtitle: {
		fontSize: 13,
		color: "#999",
		marginBottom: 16,
		lineHeight: 18,
	},
	certsList: {
		gap: 12,
	},
	certItem: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
	},
	certInfo: {
		flex: 1,
	},
	certName: {
		fontSize: 15,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 4,
	},
	certDetails: {
		fontSize: 13,
		color: "#999",
		marginBottom: 2,
	},
	certExpiry: {
		fontSize: 13,
		color: "#666",
	},
	certExpired: {
		color: "#FF453A",
	},
	deleteCertButton: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#FF453A15",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 6,
	},
	deleteCertText: {
		color: "#FF453A",
		fontSize: 13,
		marginLeft: 4,
	},
	confirmModalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1100,
	},
	confirmModal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		width: "90%",
		maxWidth: 400,
		overflow: "hidden",
	},
	confirmModalContent: {
		padding: 24,
		alignItems: "center",
	},
	confirmModalTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginTop: 16,
		marginBottom: 8,
	},
	confirmModalText: {
		fontSize: 14,
		color: "#999",
		textAlign: "center",
		marginBottom: 16,
		lineHeight: 20,
	},
	certNameText: {
		fontSize: 14,
		color: "#fff",
		fontFamily: "monospace",
		backgroundColor: "#333",
		padding: 8,
		borderRadius: 6,
		width: "100%",
		textAlign: "center",
	},
	confirmModalActions: {
		flexDirection: "row",
		borderTopWidth: 1,
		borderTopColor: "#333",
	},
	cancelButton: {
		flex: 1,
		padding: 16,
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#2A2A2A",
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	deleteButton: {
		flex: 1,
		padding: 16,
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#2A2A2A",
		borderLeftWidth: 1,
		borderLeftColor: "#333",
	},
	deleteButtonText: {
		color: "#FF453A",
		fontSize: 14,
		fontWeight: "600",
	},
	emptyProfileContainer: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 6,
		padding: 8,
		paddingHorizontal: 12,
	},
	emptyProfileText: {
		color: "#666",
		fontSize: 13,
		fontStyle: "italic",
	},
	bundleIdContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	bundleIdInput: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 6,
		padding: 8,
		paddingHorizontal: 12,
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	bundleIdButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 4,
		backgroundColor: "#0A84FF15",
		padding: 8,
		paddingHorizontal: 12,
		borderRadius: 6,
	},
	bundleIdButtonDisabled: {
		opacity: 0.5,
	},
	bundleIdButtonText: {
		color: "#0A84FF",
		fontSize: 13,
		fontWeight: "500",
	},
	certDropdownItemSelected: {
		backgroundColor: "#0A84FF15",
	},
	certDropdownItemNameSelected: {
		color: "#0A84FF",
		fontWeight: "600",
	},
	certDropdownItemExpirySelected: {
		color: "#82C91E",
		fontWeight: "500",
	},
	manualStepsContainer: {
		backgroundColor: "#f8f9fa",
		padding: 16,
		borderRadius: 8,
		marginTop: 16,
	},
	manualStepsHeader: {
		fontSize: 16,
		fontWeight: "600",
		color: "#1c1c1e",
		marginBottom: 12,
	},
	manualStep: {
		fontSize: 14,
		color: "#3c3c3e",
		marginBottom: 8,
		lineHeight: 20,
	},
	helpButton: {
		backgroundColor: "#0A84FF15",
		padding: 12,
		borderRadius: 6,
		marginTop: 16,
		alignItems: "center",
	},
	helpButtonText: {
		color: "#0A84FF",
		fontSize: 14,
		fontWeight: "500",
	},
	manualStepNote: {
		fontSize: 14,
		color: "#666",
		marginTop: 16,
		fontStyle: "italic",
	},
	iconContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		marginTop: 12,
	},
	iconItem: {
		width: "30%",
		alignItems: "center",
	},
	iconPlaceholder: {
		width: 80,
		height: 80,
		borderRadius: 16, // iOS-like rounded corners
		backgroundColor: "#333",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: 8,
		overflow: "hidden", // Ensure the image respects border radius
	},
	iconPreviewContainer: {
		backgroundColor: "transparent",
		borderWidth: 1,
		borderColor: "#444",
	},
	iconPreview: {
		width: "100%",
		height: "100%",
		borderRadius: 16, // Match the container border radius
	},
	iconLabel: {
		color: "#fff",
		fontSize: 12,
		marginBottom: 4,
		textAlign: "center",
	},
	uploadIconButton: {
		paddingHorizontal: 16,
		paddingVertical: 6,
		backgroundColor: "#0A84FF15",
		borderRadius: 6,
		minWidth: 80,
		alignItems: "center",
	},
	uploadingIconButton: {
		opacity: 0.7,
	},
	uploadIconText: {
		color: "#0A84FF",
		fontSize: 12,
		fontWeight: "500",
	},
	iconError: {
		color: "#FF453A",
		fontSize: 10,
		textAlign: "center",
		marginBottom: 6,
		maxWidth: "90%",
	},
	requiredText: {
		color: "#FF453A",
		fontWeight: "bold",
	},
	optionalText: {
		color: "#666",
		fontSize: 10,
		fontStyle: "italic",
	},
	iconHelp: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 8,
		marginTop: 4,
	},
	iconHelpText: {
		color: "#666",
		fontSize: 12,
		marginLeft: 6,
	},
	saveIconsContainer: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "flex-end",
		marginTop: 16,
	},
	saveIconsButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 6,
		paddingVertical: 8,
		paddingHorizontal: 16,
		flexDirection: "row",
		alignItems: "center",
	},
	saveIconsButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	saveIconsButtonSuccess: {
		backgroundColor: "#34C759",
	},
	saveIconsText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	savedIcon: {
		marginLeft: 8,
	},
	changelogContainer: {
		marginTop: 8,
	},
	changelogContentContainer: {
		flexDirection: "row",
	},
	changelogInput: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
		textAlignVertical: "top",
		minHeight: 80,
		marginRight: 12,
	},
	changelogDisplayContainer: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		minHeight: 80,
		marginRight: 12,
	},
	changelogText: {
		color: "#fff",
		fontSize: 14,
	},
	changelogButtonContainer: {
		justifyContent: "flex-start",
		alignItems: "center",
		gap: 8,
	},
	changelogButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 6,
		backgroundColor: "#0A84FF15",
	},
	changelogSaveButton: {
		backgroundColor: "#0A84FF",
	},
	changelogButtonDisabled: {
		opacity: 0.5,
	},
	changelogButtonSuccess: {
		backgroundColor: "#34C759",
	},
	changelogButtonText: {
		color: "#0A84FF",
		fontSize: 14,
		fontWeight: "500",
	},
	changelogSaveButtonText: {
		color: "#fff",
	},
	changelogCancelButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 6,
		backgroundColor: "#33333350",
	},
	changelogCancelButtonText: {
		color: "#666",
		fontSize: 14,
		fontWeight: "500",
	},
});

export default AppleDeveloperService;
